import { z } from 'zod';

import { ColorSchema } from '#core/schema/ColorSchema.ts';

/**
 * Trend direction for KPI value changes.
 */
export const KPI_TRENDS = ['up', 'down', 'neutral'] as const;

export type KpiTrend = (typeof KPI_TRENDS)[number];

/**
 * Alert variants for KPI display.
 * - emergency: Pulsing red glow with alert icon (critical alerts)
 * - corrective: Orange/yellow gradient (action required)
 * - preventive: Blue gradient (informational warning)
 */
export const KPI_KINDS = ['emergency', 'corrective', 'preventive'] as const;

export type KpiKind = (typeof KPI_KINDS)[number];

/**
 * Data shape for pure KPI (single value) rendering.
 * Displays a prominent metric with optional trend indicator.
 *
 * Used by both dashboard KpiComponent and chat KpiBlock.
 */
export const KpiDataSchema = z.object({
  value: z.union([z.number(), z.string()]).describe('The primary metric value to display prominently'),
  label: z.string().describe('Label describing what the value represents'),
  unit: z.string().optional().describe('Unit of measurement (e.g., "kWh", "%", "items")'),
  trend: z
    .enum(KPI_TRENDS)
    .optional()
    .describe('Direction indicator: up (positive change), down (negative), neutral (no change)'),
  trendValue: z.number().optional().describe('Percentage or absolute change value to display with trend'),
});

export type KpiData = z.infer<typeof KpiDataSchema>;

/**
 * Unified KPI component schema for LLM and dashboard use.
 *
 * Supports two display variants:
 * 1. Standard KPI: Clean metric display with optional trend indicators
 * 2. Alert KPI: Colored gradient background with optional emergency styling
 *
 * Use `color` and/or `kind` props to enable alert-style display with gradient backgrounds.
 * Without these props, renders as a standard bordered KPI card.
 *
 * Strict schema - all props must be explicitly defined.
 */
export const KpiSchema = z.object({
  type: z.literal('kpi').describe('KPI component type'),
  value: z.union([z.number(), z.string()]).describe('Primary metric value to display'),
  label: z.string().describe('Short ~5-word label describing the metric'),
  unit: z.string().optional().describe('Unit of measurement (e.g., "kWh", "%", "items")'),
  trend: z.enum(KPI_TRENDS).optional().describe('Trend direction: up, down, or neutral'),
  trendValue: z.number().optional().describe('Change percentage to display with trend'),
  color: ColorSchema.optional().describe(
    'Alert background color (e.g., "red", "orange", "green"). Enables alert-style display',
  ),
  kind: z
    .enum(KPI_KINDS)
    .optional()
    .describe('Alert variant: "emergency" (pulsing red with icon), "corrective" (orange), "preventive" (blue)'),
  decimalScale: z
    .number()
    .optional()
    .describe('Decimal places for numeric values (default: 2 for standard, 1 for alert)'),
});

/** @public Input type for KpiSchema - use for component props */
export type KpiSchemaInput = z.input<typeof KpiSchema>;

/** @public Output type for KpiSchema - use after Zod parsing */
export type KpiSchemaOutput = z.output<typeof KpiSchema>;
